import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { DateRangeFilter } from 'components/DateRangeFilter';
import { ProjectMethodology } from 'components/ProjectMethodology';
import { getStageLabel } from 'components/ProjectStage';
import { getStatusLabel, ProjectStatus } from 'components/ProjectStatus';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import type { CompanyData, ProjectItemData, ProjectStatusEnum, ProjectStageEnum } from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { getMethodologyLabel } from 'types/methodology';
import { formatDateToLocale } from 'utils';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import {
    projectAuthoredByFilterGql,
    projectMethodologyFilterGql,
    projectNameFilterGql,
    projectStageFilterGql,
    projectStatusFilterGql,
    projectSubsidiaryFilterGql,
} from '../projectListFilterQueries';
import { ColumnPairs } from '../projectListHelpers';

const subsidiaryNameWhereVariablesGetter = (value: string) => ({
    value: {
        name: {
            containsi: value,
        },
    },
});

const updateMethodologyItems = (arr: { value: string | null }[]) =>
    arr?.filter((i) => i.value !== ProjectMethodologyEnum.Etr);

export const useProjectListColumns = () => {
    const columns = useMemo<ColDef<ProjectItemData>[]>(
        () => [
            {
                field: 'name',
                headerName: ColumnPairs.name,
                sortable: true,
                flex: 2,
                minWidth: 200,
                cellRenderer: ({ data }: { data: ProjectItemData }) => {
                    if (!data) {
                        return '';
                    }

                    const loweredMethodology = data.methodology.toLowerCase();

                    return (
                        <Link className="linkName" to={`/projects/${loweredMethodology}/${data.uniqueId}`}>
                            {data.name}
                        </Link>
                    );
                },
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    dataAccessor: 'projectsNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.name,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: projectNameFilterGql,
                },
            },
            {
                field: 'methodology',
                headerName: ColumnPairs.methodology,
                cellRenderer: ({ data }: { data: ProjectItemData }) =>
                    data ? <ProjectMethodology methodology={data?.methodology} /> : '',
                flex: 1,
                minWidth: 200,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    groupAndSortItemsHelper: updateMethodologyItems,
                    dataAccessor: 'projectsMethodologyFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.methodology,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: ProjectMethodologyEnum }) =>
                                getMethodologyLabel(value),
                        },
                    ],
                    query: projectMethodologyFilterGql,
                },
            },
            {
                field: 'stage',
                headerName: ColumnPairs.stage,
                valueFormatter: ({ value }) => getStageLabel(value),
                flex: 1,
                minWidth: 200,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    dataAccessor: 'projectsStageFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.stage,
                            field: 'value',
                            checkboxSelection: true,
                            valueFormatter: ({ value }: { value: ProjectStageEnum }) => getStageLabel(value),
                            width: 400,
                        },
                    ],
                    query: projectStageFilterGql,
                },
            },
            {
                field: 'status',
                headerName: ColumnPairs.status,
                cellRenderer: ({ data }: { data: ProjectItemData }) => <ProjectStatus id={data?.status} />,
                flex: 1,
                minWidth: 200,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    dataAccessor: 'projectsStatusFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.status,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: ProjectStatusEnum }) => getStatusLabel(value),
                        },
                    ],
                    query: projectStatusFilterGql,
                },
            },
            {
                field: 'updatedAt',
                headerName: ColumnPairs.updatedAt,
                valueFormatter: ({ value }) => formatDateToLocale(value),
                flex: 1,
                minWidth: 200,
                filter: DateRangeFilter,
            },
            {
                field: 'authoredBy',
                headerName: ColumnPairs.authoredBy,
                flex: 1,
                minWidth: 200,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    dataAccessor: 'projectsAuthoredByFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.authoredBy,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: projectAuthoredByFilterGql,
                },
            },
            {
                field: 'subsidiaryName',
                flex: 1,
                minWidth: 200,
                headerName: ColumnPairs.subsidiaryName,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    customWhereVariablesGetter: subsidiaryNameWhereVariablesGetter,
                    dataAccessor: 'projectsSubsidiaryFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.subsidiaryName,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: CompanyData }) => value?.name,
                        },
                    ],
                    query: projectSubsidiaryFilterGql,
                },
            },
        ],
        [],
    );

    const [selection, , , setSelection] = useSelection('projectList');
    const [pinnedItems, , , setPinned] = usePin('projectList');

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                const newColumn = { ...column } as ColDef & { disabled: boolean };

                if (idx < 2) {
                    newColumn.disabled = true;
                }

                if (pinnedItems[getColumnId(column)]) {
                    newColumn.pinned = true;
                }

                acc.push(newColumn);

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const tableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setSelection, setPinned],
    );

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    return {
        columns: fullSetColumns,
        fullSetSettingsColumns,
        isColumnVisible,
        tableSettingsConfirm,
    };
};
//
